import { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useMsal, AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { ErrorBoundary, Skeleton } from '@babomrs/ui';
import { apiClient } from '@babomrs/api-client';
import { ClientAuthContext, type ClientUser } from './hooks/useClientAuth';
import { loginRequest } from './hooks/msalConfig';
import { PublicLayout } from './layouts/PublicLayout';
import { ClientAppShell } from './layouts/ClientAppShell';
import { ActiveEntityProvider } from './hooks/useActiveEntity';
import { InDevelopmentTile } from './components/InDevelopmentTile';
import { AccountOutcomePage, SignInRequired, classifyMeFailure, type PortalLoadFailure } from './pages/SignInPage';

// Public marketing pages
const PublicHome = lazy(() => import('./pages/public/HomePage'));
const AboutPage = lazy(() => import('./pages/public/AboutPage'));
const ServicesPage = lazy(() => import('./pages/public/ServicesPage'));
const ContactPage = lazy(() => import('./pages/public/ContactPage'));
const PrivacyPage = lazy(() => import('./pages/public/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/public/TermsPage'));

// Authenticated portal pages
const PortalHome = lazy(() => import('./pages/HomePage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const SigningPage = lazy(() => import('./pages/SigningPage'));
const SigningRequestDetail = lazy(() => import('./pages/SigningRequestDetail'));
const QueriesPage = lazy(() => import('./pages/QueriesPage'));
const QueryDetail = lazy(() => import('./pages/QueryDetail'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
const BookAppointmentPage = lazy(() => import('./pages/BookAppointmentPage'));
const CommsPreferencesPage = lazy(() => import('./pages/CommsPreferencesPage'));
const ClientHelpCentrePage = lazy(() => import('./pages/ClientHelpCentrePage'));
// Story #26236 — Notifications, opened from the bell (BR-CP-HOME-040).
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));

// Standalone token-authenticated KYC questionnaire — rendered OUTSIDE the MSAL portal guard.
const KycPortalPage = lazy(() => import('./pages/aml/kyc/KycPortalPage'));

const isDevMode = import.meta.env.VITE_AUTH_MODE === 'dev' || import.meta.env.DEV;

export function App() {
  const [user, setUser] = useState<ClientUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isDevMode) {
      // Production: /me fetch happens after MSAL signs in (PortalLoader).
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const me = await apiClient.get<ClientUser>('/api/v1/client/me');
        if (!cancelled) setUser(me);
      } catch {
        // The portal route's PortalLoader asks again and shows the outcome page (BR-CP-ACC-005), as in production.
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const isAuthenticated = !!user;

  return (
    <ClientAuthContext.Provider value={{ user, isAuthenticated, isLoading }}>
      <ErrorBoundary>
        <BrowserRouter>
          <Routes>
            {/* Public marketing site */}
            <Route element={<PublicLayout isAuthenticated={isAuthenticated} />}>
              <Route index element={<PublicSuspense><PublicHome /></PublicSuspense>} />
              <Route path="/about" element={<PublicSuspense><AboutPage /></PublicSuspense>} />
              <Route path="/services" element={<PublicSuspense><ServicesPage /></PublicSuspense>} />
              <Route path="/contact" element={<PublicSuspense><ContactPage /></PublicSuspense>} />
              {/* Story #29994: the Privacy Policy and Terms of Use (the Intuit apps' privacy and EULA URLs, BR-CP-PUB-065). */}
              <Route path="/privacy" element={<PublicSuspense><PrivacyPage /></PublicSuspense>} />
              <Route path="/terms" element={<PublicSuspense><TermsPage /></PublicSuspense>} />
            </Route>

            {/* Authenticated portal */}
            <Route
              path="/portal/*"
              element={<PortalRoutes user={user} setUser={setUser} />}
            />

            <Route path="/login" element={<LoginRedirect />} />

            {/* Standalone token-gated KYC questionnaire — no MSAL, outside /portal/* */}
            <Route path="/aml/kyc" element={<PublicSuspense><KycPortalPage /></PublicSuspense>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </ErrorBoundary>
    </ClientAuthContext.Provider>
  );
}

function PublicSuspense({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<div className="max-w-6xl mx-auto px-4 sm:px-6 py-20"><Skeleton className="h-64 w-full" /></div>}>
      {children}
    </Suspense>
  );
}

function LoginRedirect() {
  const { instance } = useMsal();
  useEffect(() => {
    if (isDevMode) {
      window.location.href = '/portal';
      return;
    }
    instance.loginRedirect(loginRequest).catch((err) => console.error('Login failed:', err));
  }, [instance]);
  return null;
}

interface PortalRoutesProps {
  user: ClientUser | null;
  setUser: (u: ClientUser | null) => void;
}

function PortalRoutes({ user, setUser }: PortalRoutesProps) {
  if (isDevMode) {
    // Dev mode has no MSAL; the same PortalLoader decides the outcome page as in production (BR-CP-ACC-005).
    return (
      <PortalLoader user={user} setUser={setUser}>
        <AuthenticatedPortal />
      </PortalLoader>
    );
  }
  return (
    <>
      <AuthenticatedTemplate>
        <PortalLoader user={user} setUser={setUser}>
          <AuthenticatedPortal />
        </PortalLoader>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SignInRequired />
      </UnauthenticatedTemplate>
    </>
  );
}

/**
 * Exported for test: this component owns the decision about what a client is TOLD when their
 * account cannot be loaded, which is the behaviour worth pinning. `App` itself mounts its own
 * BrowserRouter, so rendering the whole app inside the test harness's MemoryRouter would nest two
 * routers and test nothing useful.
 */
export function PortalLoader({
  user, setUser, children,
}: {
  user: ClientUser | null;
  setUser: (u: ClientUser | null) => void;
  children: React.ReactNode;
}) {
  const [loading, setLoading] = useState(!user);
  // Null until we know WHY. Previously there was no such state and the render below passed a
  // hardcoded `isPending`, so every failure — 500, timeout, dead API — was reported to the client
  // as "Account Pending Activation".
  const [failure, setFailure] = useState<PortalLoadFailure | null>(null);
  // Bumping this re-runs the effect. Without it the fetch ran ONCE: a client who hit a transient
  // failure stayed on the error screen for ever, because nothing asked again even after the API
  // recovered. That is how a 90-second outage became an indefinitely stuck portal.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (user) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const me = await apiClient.get<ClientUser>('/api/v1/client/me');
        if (cancelled) return;
        setUser(me);
        setFailure(null);
      } catch (err) {
        if (cancelled) return;
        // BR-CP-ACC-005: 404 = no portal record; 403 = an account answer only when its problem type says which
        // (suspended, or bound to another sign-in); ANYTHING ELSE — including a network failure, where status is
        // undefined, and an unrecognised 403 — is OUR problem, not the client's, and is retryable.
        setFailure(classifyMeFailure(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, setUser, attempt]);

  if (loading) {
    return (
      // The prototype's sign-in Loading state: one 192 x 24 skeleton centred in the gate (`.gate`, Story #28003).
      <div className="p-4 sm:p-6">
        <div className="flex min-h-[70vh] items-center justify-center" role="status" aria-label="Signing you in">
          <Skeleton className="w-48 h-6" />
        </div>
      </div>
    );
  }
  if (!user) {
    return (
      <AccountOutcomePage
        variant={failure ?? 'error'}
        onRetry={() => setAttempt((n) => n + 1)}
      />
    );
  }
  return <>{children}</>;
}

function AuthenticatedPortal() {
  const pageFallback = <Skeleton className="h-64 w-full" />;
  // Spec §7 — the active entity, inside PortalLoader (so the person is known) and around every portal route.
  return (
    <ActiveEntityProvider>
      <Routes>
        <Route element={<ClientAppShell />}>
          <Route index element={<Suspense fallback={pageFallback}><PortalHome /></Suspense>} />
          <Route path="onboarding" element={<Suspense fallback={pageFallback}><OnboardingPage /></Suspense>} />
          <Route path="signing" element={<Suspense fallback={pageFallback}><SigningPage /></Suspense>} />
          <Route path="signing/:id" element={<Suspense fallback={pageFallback}><SigningRequestDetail /></Suspense>} />
          <Route path="queries" element={<Suspense fallback={pageFallback}><QueriesPage /></Suspense>} />
          <Route path="queries/:id" element={<Suspense fallback={pageFallback}><QueryDetail /></Suspense>} />
          <Route path="documents" element={<Suspense fallback={pageFallback}><DocumentsPage /></Suspense>} />
          <Route path="book-appointment" element={<Suspense fallback={pageFallback}><BookAppointmentPage /></Suspense>} />
          <Route path="comms-preferences" element={<Suspense fallback={pageFallback}><CommsPreferencesPage /></Suspense>} />
          <Route path="notifications" element={<Suspense fallback={pageFallback}><NotificationsPage /></Suspense>} />
          {/* BR-CP-HOME-001 / DL-X-30 D3 — in the nav from R01, each opening the standard In Development tile until its
              page ships: My companies in R11 (DL-CP-41), My details in R05 (DL-CP-19). The owning screens reuse the routes. */}
          <Route path="companies" element={<InDevelopmentTile title="My companies" release="R11" />} />
          <Route path="my-details" element={<InDevelopmentTile title="My details" release="R05" />} />
          {/* Help Centre (#10803 AC5) — searchable index of every help topic. Static
              instruction only, no client data. */}
          <Route path="help" element={<Suspense fallback={pageFallback}><ClientHelpCentrePage /></Suspense>} />
          <Route path="*" element={<Navigate to="/portal" replace />} />
        </Route>
      </Routes>
    </ActiveEntityProvider>
  );
}
